import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import {
  AlertDialog, AlertDialogAction, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Bell } from "@phosphor-icons/react";

const MESSAGE = "Alza la serranda e accendi la TV";
// Mostra l'allarme una sola volta per apertura dell'app (reset ad ogni ricarica pagina)
let alarmShownThisLoad = false;

function startAlarm() {
  let ctx;
  const cleanup = [];

  const playSiren = () => {
    try {
      if (ctx.state === "suspended") ctx.resume();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "square";
      gain.gain.value = 0.6; // volume alto, tipo allarme
      osc.connect(gain);
      gain.connect(ctx.destination);
      const now = ctx.currentTime;
      // sirena bitonale per 3 secondi
      for (let t = 0; t < 3; t += 0.5) {
        osc.frequency.setValueAtTime(880, now + t);
        osc.frequency.setValueAtTime(620, now + t + 0.25);
      }
      osc.start(now);
      osc.stop(now + 3);
      cleanup.push(() => { try { osc.stop(); } catch (e) { /* noop */ } });
    } catch (e) { /* noop */ }
  };

  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    ctx = new AC();
    if (ctx.state === "suspended") {
      // se l'autoplay è bloccato, avvia al primo gesto dell'utente
      const resume = () => {
        ctx.resume().then(playSiren).catch(() => {});
        document.removeEventListener("click", resume);
        document.removeEventListener("keydown", resume);
      };
      document.addEventListener("click", resume);
      document.addEventListener("keydown", resume);
      cleanup.push(() => {
        document.removeEventListener("click", resume);
        document.removeEventListener("keydown", resume);
      });
      ctx.resume().then(playSiren).catch(() => {});
    } else {
      playSiren();
    }
  } catch (e) { /* noop */ }

  return () => {
    cleanup.forEach((f) => f());
    try { if (ctx) ctx.close(); } catch (e) { /* noop */ }
  };
}

export default function WakeAlarm() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const stopRef = useRef(null);

  useEffect(() => {
    if (!user || user.role !== "dipendente") return;
    if (alarmShownThisLoad) return;
    alarmShownThisLoad = true;
    setOpen(true);
    stopRef.current = startAlarm();
    return () => { if (stopRef.current) stopRef.current(); };
  }, [user]);

  const handleClose = () => {
    if (stopRef.current) stopRef.current();
    stopRef.current = null;
    setOpen(false);
  };

  const replay = () => {
    if (stopRef.current) stopRef.current();
    stopRef.current = startAlarm();
  };

  return (
    <AlertDialog open={open}>
      <AlertDialogContent
        data-testid="wake-alarm-dialog"
        onEscapeKeyDown={(e) => e.preventDefault()}
        className="border-[#F97316]"
      >
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2 text-[#F97316]">
            <Bell size={24} weight="fill" className="animate-pulse" /> Promemoria apertura
          </AlertDialogTitle>
          <AlertDialogDescription className="text-lg font-semibold text-foreground pt-2">
            {MESSAGE}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <button
            type="button"
            onClick={replay}
            data-testid="wake-alarm-replay"
            className="text-sm px-4 py-2 rounded-md border border-border hover:bg-secondary transition-colors"
          >
            Riproduci di nuovo
          </button>
          <AlertDialogAction onClick={handleClose} data-testid="wake-alarm-close" className="bg-[#F97316] hover:bg-[#ea6a0c]">
            Ho capito
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
